import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useOrderPresence } from '../hooks/useOrderPresence';

// Floating customer ↔ team chat bubble, bound to a single order. Unlike the
// read-only message list in the order card, this lets the customer actually
// reply. Mounting it also marks the customer "live" (presence) so admins are
// nudged that someone might want a reply.
export default function CustomerChatBubble({ orderId, orderLabel }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const scrollerRef = useRef(null);
  const seenAdminCountRef = useRef(0); // admin messages acknowledged by last open

  // Keep the customer flagged as live while this bubble is mounted.
  useOrderPresence(orderId, { enabled: !!orderId });

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMessages(data.messages || []);
      setAdminOnline(!!data.adminOnline);
    } catch {
      // silent — transient poll failures shouldn't spam the customer
    }
  };

  // Poll while mounted so the unread badge stays fresh even when collapsed.
  useEffect(() => {
    if (!orderId) return undefined;
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Auto-scroll to newest when open.
  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length, open]);

  // When the panel is open, treat all current admin messages as seen.
  const adminCount = messages.filter(m => m.senderType === 'admin').length;
  useEffect(() => {
    if (open) seenAdminCountRef.current = adminCount;
  }, [open, adminCount]);

  const unseen = open ? 0 : Math.max(0, adminCount - seenAdminCountRef.current);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { body: text });
      setMessages(m => [...m, data]);
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!orderId) return null;

  const label = (m) =>
    m.senderType === 'admin' ? '✦ Aloha Amino Team' :
    m.senderType === 'system' ? 'System' : 'You';

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end">
      {/* Panel */}
      {open && (
        <div
          className="mb-3 w-[22rem] max-w-[calc(100vw-3rem)] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          style={{ height: '30rem', background: '#0b0720', border: '1px solid rgba(212,175,55,0.4)' }}
        >
          {/* Header */}
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, #1a1038, #0b0720)', borderBottom: '1px solid rgba(212,175,55,0.25)' }}
          >
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: '#d4af37' }}>
                Chat with our team
              </div>
              <div className="text-[11px] mt-0.5 flex items-center gap-1.5" style={{ color: '#7a5c30' }}>
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: adminOnline ? '#4ade80' : '#6b7280', boxShadow: adminOnline ? '0 0 6px #4ade80' : 'none' }}
                />
                {adminOnline ? 'Agent online' : 'Leave a message — we reply fast'}
                {orderLabel && <span className="opacity-70">· {orderLabel}</span>}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-xl leading-none hover:opacity-70"
              style={{ color: '#7a5c30' }}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 ? (
              <div className="text-center text-xs italic py-10" style={{ color: '#5a3a18' }}>
                No messages yet. Say hello — our team is here to help with your order.
              </div>
            ) : (
              messages.map(m => {
                const fromYou = m.senderType === 'customer';
                return (
                  <div key={m.id} className={'flex ' + (fromYou ? 'justify-end' : 'justify-start')}>
                    <div
                      className="max-w-[85%] rounded-2xl px-3 py-2"
                      style={{
                        background: fromYou ? 'rgba(212,175,55,0.14)' : 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(212,175,55,0.15)',
                      }}
                    >
                      <div className="text-[10px] uppercase tracking-wide font-bold mb-0.5" style={{ color: fromYou ? '#d4af37' : '#9a8050' }}>
                        {label(m)}
                      </div>
                      <div className="text-sm whitespace-pre-wrap break-words" style={{ color: '#e8d9b8' }}>
                        {m.body}
                      </div>
                      {m.isPaymentClaim && (
                        <div className="mt-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: '#f5c842' }}>
                          ✦ Payment confirmation
                        </div>
                      )}
                      <div className="text-[9px] mt-1" style={{ color: '#5a3a18' }}>
                        {new Date(m.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Composer */}
          <div className="p-3 flex items-end gap-2" style={{ borderTop: '1px solid rgba(212,175,55,0.2)' }}>
            <textarea
              rows={2}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a message…"
              maxLength={2000}
              className="flex-1 resize-none rounded-lg text-sm px-3 py-2 focus:outline-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,175,55,0.25)', color: '#e8d9b8' }}
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending}
              className="shrink-0 px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #b8860b, #d4af37)', color: '#0a0718' }}
            >
              {sending ? '…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* Launcher */}
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-2xl hover:scale-105 transition-transform"
        style={{ background: 'linear-gradient(135deg, #b8860b, #d4af37, #f5c842)', color: '#0a0718' }}
        aria-label="Open chat with our team"
      >
        💬
        {unseen > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
            {unseen}
          </span>
        )}
      </button>
    </div>
  );
}
